import api from "./api";

const REPORT_ENDPOINT = "/performance/appraisals/report";
const REPORT_FALLBACK_ENDPOINT = "/performance/appraisals/report/overview";
const ACTIONS_ENDPOINT = "/performance/appraisals/actions";
const LEGACY_LIST_ENDPOINT = "/performance/appraisals";

const ACTION_TYPES = ["promotion", "increment", "bonus"];
let reportEndpointMissing = false;
let reportEndpointMissingAt = 0;
const REPORT_ENDPOINT_RETRY_MS = 60 * 1000;
const QUIET_REQUEST_CONFIG = {
  suppress404Toast: true,
  suppress500Toast: true,
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toPercentage = (value) => {
  const n = toFiniteNumber(value);
  if (n === null) return null;
  if (n >= 0 && n <= 5) return Math.round(n * 20 * 10) / 10;
  return Math.round(n * 10) / 10;
};

const toScoreOutOf5 = (value) => {
  const n = toFiniteNumber(value);
  if (n === null) return null;
  if (n >= 0 && n <= 5) return Math.round(n * 100) / 100;
  return Math.round((n / 20) * 100) / 100;
};

const deriveBand = (percentage) => {
  const p = toFiniteNumber(percentage);
  if (p === null) return "<60";
  if (p >= 90) return "90-100";
  if (p >= 80) return "80-89";
  if (p >= 70) return "70-79";
  if (p >= 60) return "60-69";
  return "<60";
};

const toAction = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return ACTION_TYPES.includes(normalized) ? normalized : "none";
};

const cleanParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    out[key] = value;
  });
  return out;
};

const mapOverviewParams = (params = {}) => {
  const out = {};
  if (params.from) out.date_from = params.from;
  if (params.to) out.date_to = params.to;
  if (params.status) out.status = params.status;
  return out;
};

const pickCurrentStep = (row, stepInstances = []) => {
  const currentOrder = Number(row?.current_step_order || 0);
  if (currentOrder > 0) {
    const byOrder = stepInstances.find((s) => Number(s?.step_order || 0) === currentOrder);
    if (byOrder) return byOrder;
  }

  const pending = stepInstances.find((s) => {
    const status = String(s?.status || "").toLowerCase();
    return status === "pending" || status === "in_progress";
  });

  return pending || stepInstances[stepInstances.length - 1] || null;
};

const toNonEmptyString = (value) => {
  if (typeof value !== "string") return null;
  const out = value.trim();
  return out ? out : null;
};

const isSelfStep = (step) => {
  const stepKey = String(step?.step_key || "").trim().toLowerCase();
  const actorType = String(step?.actor_type || "").trim().toLowerCase();
  const stepName = String(step?.step_name || step?.name || "").trim().toLowerCase();

  return Boolean(step?.is_self_review) || stepKey === "self" || actorType === "self" || stepName.includes("self review");
};

const buildSetByFromStep = (step) => {
  const user = step?.user || {};
  const id = user?.id ?? step?.user_id ?? null;
  const name = toNonEmptyString(user?.name);
  const role = toNonEmptyString(user?.actual_role || user?.role || step?.actor_type);

  if (id === null && !name && !role) return null;

  return {
    id,
    name: name || "System",
    role: role || "system",
  };
};

const inferRecommendationSetBy = (row) => {
  const stepInstances = Array.isArray(row?.step_instances)
    ? row.step_instances
    : Array.isArray(row?.stepInstances)
    ? row.stepInstances
    : [];

  if (!stepInstances.length) return null;

  let best = null;
  let bestTs = -1;
  let bestOrder = -1;

  stepInstances.forEach((step) => {
    if (!step || typeof step !== "object") return;
    if (isSelfStep(step)) return;

    const status = String(step?.status || "").trim().toLowerCase();
    const submittedAt = step?.submitted_at || step?.latest_submission?.submitted_at || null;
    const ts = submittedAt ? Date.parse(submittedAt) : 0;

    if (status !== "submitted" && status !== "completed" && !(Number.isFinite(ts) && ts > 0)) {
      return;
    }

    const candidate = buildSetByFromStep(step);
    if (!candidate) return;

    const order = Number(step?.step_order || 0);
    const safeTs = Number.isFinite(ts) ? ts : 0;

    if (!best || safeTs > bestTs || (safeTs === bestTs && order > bestOrder)) {
      best = candidate;
      bestTs = safeTs;
      bestOrder = order;
    }
  });

  if (best) return best;

  const codeCandidates = [toNonEmptyString(row?.supervisor_code), toNonEmptyString(row?.final_reviewer_code)].filter(Boolean);
  for (const code of codeCandidates) {
    for (const step of stepInstances) {
      if (!step || typeof step !== "object") continue;
      if (isSelfStep(step)) continue;
      if (String(step?.user?.employee_code || "") !== String(code)) continue;
      const candidate = buildSetByFromStep(step);
      if (candidate) return candidate;
    }
  }

  return null;
};

const toLegacyRecommendations = (row) => {
  const out = [];
  const stepKey = "overall_review";
  const inferredSetBy = inferRecommendationSetBy(row);
  const setBy = {
    id: inferredSetBy?.id ?? row?.updated_by?.id ?? row?.employee?.id ?? null,
    name: inferredSetBy?.name || row?.updated_by?.name || row?.employee?.name || "System",
    role:
      inferredSetBy?.role ||
      row?.updated_by?.role ||
      row?.updated_by?.actual_role ||
      row?.employee?.actual_role ||
      "system",
  };

  const notes = String(row?.recommendation_remarks || row?.overall_details?.remarks || "").trim();

  if (row?.recommendation_promotion || row?.recommend_promotion) {
    out.push({
      id: `legacy-${row.id}-promotion`,
      step_key: stepKey,
      set_by: setBy,
      action_type: "promotion",
      amount: null,
      percent: null,
      new_position_id: row?.new_position_id || null,
      notes,
      created_at: row?.updated_at || row?.created_at || null,
      status: "proposed",
    });
  }

  if (row?.recommendation_increment || row?.recommend_increment) {
    out.push({
      id: `legacy-${row.id}-increment`,
      step_key: stepKey,
      set_by: setBy,
      action_type: "increment",
      amount: toFiniteNumber(row?.increment_amount || row?.recommend_increment_amount || null),
      percent: null,
      new_position_id: null,
      notes,
      created_at: row?.updated_at || row?.created_at || null,
      status: "proposed",
    });
  }

  if (row?.recommendation_bonus || row?.recommend_bonus) {
    out.push({
      id: `legacy-${row.id}-bonus`,
      step_key: stepKey,
      set_by: setBy,
      action_type: "bonus",
      amount: toFiniteNumber(row?.bonus_amount || row?.recommend_bonus_amount || null),
      percent: null,
      new_position_id: null,
      notes,
      created_at: row?.updated_at || row?.created_at || null,
      status: "proposed",
    });
  }

  return out;
};

const toLegacyItem = (row) => {
  const employee = row?.employee || {};
  const project = row?.project || employee?.project || null;
  const workflowTemplate = row?.workflow_template || row?.workflowTemplate || null;
  const stepInstances = Array.isArray(row?.step_instances)
    ? row.step_instances
    : Array.isArray(row?.stepInstances)
    ? row.stepInstances
    : [];

  const sortedSteps = [...stepInstances].sort(
    (a, b) => Number(a?.step_order || 0) - Number(b?.step_order || 0)
  );

  const finalPercentage = toPercentage(
    row?.final_percentage ??
      row?.overall_percentage ??
      row?.percentage ??
      row?.overall_rating ??
      row?.overall_details?.overall_rating
  );

  const finalScoreOver5 = toScoreOutOf5(
    row?.final_score_5 ??
      row?.current_overall_rating ??
      row?.overall_rating ??
      row?.overall_details?.overall_rating ??
      finalPercentage
  );

  const previousPercentage = toPercentage(
    row?.previous_cycle?.final_percentage ??
      row?.previous_overall_rating ??
      row?.previous_overall ??
      row?.prev_overall_rating
  );

  const previousScoreOver5 = toScoreOutOf5(
    row?.previous_cycle?.final_score_5 ??
      row?.previous_overall_rating ??
      row?.previous_overall ??
      row?.prev_overall_rating ??
      previousPercentage
  );

  const currentStep = pickCurrentStep(row, sortedSteps);
  const recommendations = Array.isArray(row?.recommendations)
    ? row.recommendations
    : toLegacyRecommendations(row);

  return {
    id: row?.id,
    cycle_name: row?.cycle_name || row?.label || row?.cycle || row?.period || workflowTemplate?.name || "-",
    employee: {
      id: employee?.id || null,
      employee_code: employee?.employee_code || row?.employee_code || "-",
      name: employee?.name || "-",
      department: employee?.department || "-",
      position:
        employee?.position ||
        employee?.position_name ||
        employee?.job_title ||
        employee?.title ||
        "-",
    },
    project: project
      ? {
          id: project?.id || null,
          name: project?.name || "-",
        }
      : employee?.project_id
      ? { id: employee.project_id, name: `Project #${employee.project_id}` }
      : null,
    final_percentage: finalPercentage,
    final_score_5: finalScoreOver5,
    final_rating_label: row?.final_rating_label || row?.rating_label || null,
    status: row?.status || "draft",
    submitted_at: row?.submitted_at || null,
    workflow_template: workflowTemplate?.name || row?.workflow_template_name || "-",
    last_step:
      row?.last_step ||
      currentStep?.step_name ||
      currentStep?.name ||
      (currentStep?.step_order ? `Step ${currentStep.step_order}` : "-"),
    last_updated_at: row?.last_updated_at || row?.updated_at || null,
    previous_cycle:
      previousPercentage !== null || row?.previous_cycle
        ? {
            cycle_name: row?.previous_cycle?.cycle_name || "Previous Cycle",
            final_percentage: previousPercentage,
            final_score_5: previousScoreOver5,
            final_rating_label: row?.previous_cycle?.final_rating_label || null,
            submitted_at: row?.previous_cycle?.submitted_at || null,
          }
        : null,
    step_scores: sortedSteps.map((step) => ({
      step_key: step?.step_key || step?.actor_type || `step_${step?.step_order || "x"}`,
      step_name: step?.step_name || step?.name || step?.label || (step?.step_order ? `Step ${step.step_order}` : "Step"),
      evaluator: {
        id: step?.user?.id || step?.user_id || null,
        name: step?.user?.name || "Unassigned",
        role: step?.user?.actual_role || step?.user?.role || step?.actor_type || "-",
      },
      percentage: toPercentage(step?.percentage ?? step?.score_percentage),
      score: toFiniteNumber(step?.score ?? step?.overall_rating),
      submitted_at: step?.submitted_at || null,
    })),
    recommendations,
    changes: Array.isArray(row?.changes) ? row.changes : [],
  };
};

const buildLookupsFromItems = (items) => {
  const departments = new Map();
  const projects = new Map();
  const cycles = new Map();
  const positions = new Map();

  items.forEach((item) => {
    const depName = item?.employee?.department;
    if (depName && depName !== "-") departments.set(depName, { id: depName, name: depName });

    const project = item?.project;
    if (project?.name) {
      const key = project?.id ?? project.name;
      projects.set(String(key), { id: project?.id ?? project.name, name: project.name });
    }

    if (item?.cycle_name) cycles.set(item.cycle_name, { id: item.cycle_name, name: item.cycle_name });

    const positionName = item?.employee?.position;
    if (positionName && positionName !== "-") positions.set(positionName, { id: positionName, name: positionName });
  });

  return {
    departments: Array.from(departments.values()),
    projects: Array.from(projects.values()),
    cycles: Array.from(cycles.values()),
    positions: Array.from(positions.values()),
  };
};

const buildSummaryFromItems = (items) => {
  const total = items.length;
  const avgScore =
    total > 0
      ? Math.round(
          (items.reduce((acc, item) => acc + (toFiniteNumber(item?.final_percentage) || 0), 0) / total) * 10
        ) / 10
      : 0;

  const byBandMap = new Map([
    ["90-100", 0],
    ["80-89", 0],
    ["70-79", 0],
    ["60-69", 0],
    ["<60", 0],
  ]);

  const byActionMap = new Map([
    ["promotion", 0],
    ["increment", 0],
    ["bonus", 0],
    ["none", 0],
  ]);

  items.forEach((item) => {
    const band = deriveBand(item?.final_percentage);
    byBandMap.set(band, (byBandMap.get(band) || 0) + 1);

    const recs = Array.isArray(item?.recommendations) ? item.recommendations : [];
    const latest = [...recs]
      .filter((r) => ACTION_TYPES.includes(String(r?.action_type || "").toLowerCase()))
      .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))[0];

    const action = toAction(latest?.action_type);
    byActionMap.set(action, (byActionMap.get(action) || 0) + 1);
  });

  return {
    total,
    avg_score: avgScore,
    by_band: Array.from(byBandMap.entries()).map(([band, count]) => ({ band, count })),
    by_recommended_action: Array.from(byActionMap.entries()).map(([action, count]) => ({ action, count })),
  };
};

const fetchLegacyReport = async (params = {}) => {
  const listParams = cleanParams({
    page: params.page,
    per_page: params.per_page,
    status: params.status,
    q: params.q,
    with_details: 1,
  });

  const [listResp, overviewResp] = await Promise.allSettled([
    api.get(LEGACY_LIST_ENDPOINT, {
      params: listParams,
      ...QUIET_REQUEST_CONFIG,
    }),
    api.get(REPORT_FALLBACK_ENDPOINT, {
      params: mapOverviewParams(params),
      ...QUIET_REQUEST_CONFIG,
    }),
  ]);

  const listPayload = listResp.status === "fulfilled" ? listResp.value?.data || {} : {};
  const rawRows = Array.isArray(listPayload?.data)
    ? listPayload.data
    : Array.isArray(listPayload?.items)
    ? listPayload.items
    : [];

  const items = rawRows.map(toLegacyItem);
  const summary = buildSummaryFromItems(items);
  const overview = overviewResp.status === "fulfilled" ? overviewResp.value?.data || {} : {};
  const lookups = buildLookupsFromItems(items);
  const listTotal = Number(
    listPayload?.total ??
      listPayload?.meta?.total ??
      listPayload?.pagination?.total ??
      0
  );
  const listCurrentPage = Number(
    listPayload?.current_page ??
      listPayload?.meta?.current_page ??
      listPayload?.pagination?.current_page ??
      params.page ??
      1
  );
  const listPerPage = Number(
    listPayload?.per_page ??
      listPayload?.meta?.per_page ??
      listPayload?.pagination?.per_page ??
      params.per_page ??
      items.length ??
      25
  );
  const overviewTotal = Number(
    overview?.totals?.appraisals_total ??
      overview?.appraisals_total ??
      overview?.summary?.total ??
      0
  );

  return {
    summary,
    items,
    lookups,
    meta: {
      total: Number(listTotal || overviewTotal || summary.total || items.length),
      current_page: Number(listCurrentPage),
      per_page: Number(listPerPage),
    },
    _legacy: {
      used: true,
      overview: overview || null,
    },
  };
};

export async function fetchAppraisalsReport(params = {}) {
  const cleaned = cleanParams(params);

  if (reportEndpointMissing && Date.now() - reportEndpointMissingAt < REPORT_ENDPOINT_RETRY_MS) {
    return fetchLegacyReport(cleaned);
  }

  try {
    const { data } = await api.get(REPORT_ENDPOINT, { params: cleaned, ...QUIET_REQUEST_CONFIG });
    reportEndpointMissing = false;
    reportEndpointMissingAt = 0;
    return data;
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    if (status === 404 || status === 405 || status >= 500) {
      reportEndpointMissing = true;
      reportEndpointMissingAt = Date.now();
      return fetchLegacyReport(cleaned);
    }
    throw error;
  }
}

export async function finalizeAppraisalActions(payload) {
  const { data } = await api.post(ACTIONS_ENDPOINT, payload);
  return data;
}

export const performanceReportApi = {
  fetchReport: fetchAppraisalsReport,
  finalizeActions: finalizeAppraisalActions,
};
